import "./style.css";
import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import "swiper/css";
import "swiper/css/navigation";
import { COLORS } from './constants';
import IntroOverlay from './components/IntroOverlay';
import HomePage from './pages/HomePage';
import ScootersPage from './pages/ScootersPage';
import GalleryPage from './pages/GalleryPage';
import FAQPage from './pages/FAQPage';
import SupportPage from './pages/SupportPage';
import TestimonialsPage from './pages/TestimonialsPage';
import BlogPage from './pages/BlogPage';
import QuizPage from './pages/QuizPage';
import QuotePage from './pages/QuotePage';
import Button from './components/Button';
import SectionHeading from './components/SectionHeading';

const App = () => {
  const location = useLocation();
  const [showIntro, setShowIntro] = useState(() => {
    if (typeof window === "undefined") return false;
    const hasSeenIntro = window.sessionStorage.getItem("ll_intro_seen") === "1";
    return location.pathname === "/" && !hasSeenIntro;
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (location.pathname !== "/") {
      setShowIntro(false);
      return;
    }
    const hasSeenIntro = window.sessionStorage.getItem("ll_intro_seen") === "1";
    setShowIntro(!hasSeenIntro);
  }, [location.pathname]);

  useEffect(() => {
    const handler = () => {
      navigate("/quote");
      window.scrollTo(0, 0);
    };

    window.addEventListener("go-to-quote", handler);

    return () => {
      window.removeEventListener("go-to-quote", handler);
    };
  }, [navigate]);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/scooters', label: 'Fleet' },
    { path: '/quiz', label: 'Find Your Scooter' },
    { path: '/blog', label: 'Blog' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/faq', label: 'FAQ' },
    { path: '/support', label: 'Support' },
    { path: '/testimonials', label: 'Stories' },
  ];

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.lightSurface }}>
      <AnimatePresence>
        {showIntro && (
          <IntroOverlay
            onFinish={() => {
              window.sessionStorage.setItem("ll_intro_seen", "1");
              setShowIntro(false);
            }}
          />
        )}
      </AnimatePresence>

      {!showIntro && (
        <>
          <header className="relative z-50">
            <nav
              className={`fixed top-0 w-full transition-all duration-300 shadow-xl ${
                scrolled ? 'py-2' : 'py-5'
              }`}
              style={{
                backgroundColor: `${COLORS.secondaryBg}EE`,
                backdropFilter: 'blur(16px)',
              }}
            >
              <div className="container mx-auto px-6 flex justify-between items-center">
                <Link to="/" onClick={closeMenu} className="flex items-center gap-4 cursor-pointer">
                  <img 
                    src="/images/logo.png" 
                    loading="lazy" 
                    decoding="async" 
                    alt="Lectric Leisure Logo" 
                    className={`w-auto object-contain transition-all duration-300 ${ scrolled ? 'h-10 md:h-12' : 'h-12 md:h-14' }`} 
                  />
                  <span className="text-2xl font-serif text-[#F2EBDC] font-black">
                    Lectric<span style={{ color: COLORS.accent }}>Leisures</span>
                  </span>
                </Link>

                <div className="hidden lg:flex items-center gap-10">
                  {navItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="text-sm font-bold uppercase tracking-widest transition-colors text-white hover:text-[#C7A36A]"
                    >
                      {item.label}
                    </Link>
                  ))}
                  <Button
                    variant="primary"
                    onClick={() => navigate('/quote')}
                    className="px-6 py-2 text-sm"
                  >
                    Extras & Spares
                  </Button>
                </div>

                <button className="lg:hidden text-white" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                  {isMenuOpen ? <X size={32} /> : <Menu size={32} />}
                </button>
              </div>

              <AnimatePresence>
                {isMenuOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="lg:hidden overflow-hidden bg-[#1F332A]"
                  >
                    <div className="flex flex-col p-8 space-y-4">
                      {navItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={closeMenu}
                          className="text-white text-left font-bold py-2 hover:text-[#C7A36A] transition-colors"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </nav>
          </header>

          <main className="pt-[80px]">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/scooters" element={<ScootersPage />} />
              <Route path="/quiz" element={<QuizPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/testimonials" element={<TestimonialsPage />} />
              <Route path="/quote" element={<QuotePage />} />
            </Routes>
          </main>

          <footer className="py-20 bg-[#1F332A] text-[#F2EBDC] text-center">
            <div className="space-y-2 text-lg">
              <p className="font-bold">David Michelmore</p>
              <p>
                <a
                  href="mailto:dmm@lectricleisure.golf"
                  className="underline hover:text-[#C7A36A] transition"
                >
                  dmm@lectricleisure.golf
                </a>
              </p>
              <p>
                <a
                  href="tel:+61418174167"
                  className="hover:text-[#C7A36A] transition"
                >
                  (+61) 0418 174 167
                </a>
              </p>
            </div>

            <p className="mt-15 text-sm opacity-90">
              © 2026 Lectric Leisure. All rights reserved.
            </p>
            <p className="mt-4 text-sm opacity-50">
              Designed by{" "}
              <a
                href="https://littlerock.in"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold hover:text-[#C7A36A] transition"
              >
                Littlerock
              </a>
            </p>
          </footer>
        </>
      )}
    </div>
  );
};

export default App;
